import { Button } from 'shared/ui/Button';
import { showDefaultErrorToast } from 'shared/lib/notifications';
import { getKeys } from 'shared/lib/object';
import { FormProvider, useForm } from 'react-hook-form';
import type { Days, ScheduleDayModel, ScheduleModel } from 'entities/campaign';
import { SchedulingDayChangeable, useSubmitCampaign, useUpdateCampaignWizard } from 'entities/campaign';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { timezoneOptions } from 'shared/lib/timezones';
import { useGetManager } from 'entities/profile';
import { addSpaceToSchedule, getTimeRanges } from 'shared/lib/time-ranges';
import { isValidationErrorsModel } from 'shared/lib/api';

import { useCampaignId, useCampaignType, useCreateCampaignStoreActions, useSchedule } from './CreateCampaignStoreHook';

const HIDDEN_ERROR = 'hidden';

type StepScheduleProps = {
  onBack: () => void;
  onNext: () => void;
  onCreate: () => void;
};

const timeOptions = getTimeRanges(30).map((time) => ({
  text: addSpaceToSchedule(time),
  id: time,
}));

export function StepSchedule(props: StepScheduleProps) {
  const { onBack, onNext, onCreate } = props;
  const defaultSchedule = useSchedule();
  const campaignType = useCampaignType();
  const campaignId = useCampaignId();
  const { updateStepData } = useCreateCampaignStoreActions();
  const { mutate: mutateUpdate, isLoading: isLoadingUpdate, reset: resetUpdate } = useUpdateCampaignWizard(campaignId);
  const { mutateAsync: mutateAsyncSubmit, isLoading: isLoadingSubmit } = useSubmitCampaign(campaignId);
  const methods = useForm<ScheduleModel>({
    defaultValues: defaultSchedule,
  });
  const { setError, handleSubmit } = methods;

  const isSalesCampaignType = campaignType === 'sales';

  const timeZone = useGetManager().data?.data.timezone;
  const timeZoneValue = timezoneOptions.find((item) => item.id === timeZone)?.text;

  const isAnyDaySelected = Object.values(methods.watch()).some((item: ScheduleDayModel) => item.isSelected);

  const onSubmit = handleSubmit((schedule) => {
    resetUpdate();
    updateStepData({ schedule });

    mutateUpdate(
      { payload: { schedule } },
      {
        onSuccess: async () => {
          try {
            if (isSalesCampaignType) {
              await mutateAsyncSubmit({ payload: false });
              onCreate();

              return;
            }

            onNext();
          } catch (e) {
            showDefaultErrorToast();
          }
        },
        onError: (error) => {
          const errorData = error.response.data;

          if (!isValidationErrorsModel(errorData)) {
            return;
          }
          const errors = errorData.errors;

          for (const key of getKeys(errors)) {
            const day = key as Days;
            const error = errors[key];

            if (error) {
              setError(day, { message: error[0].message });
            }

            setError(`${day}.timeTo`, { message: HIDDEN_ERROR });
            setError(`${day}.timeFrom`, { message: HIDDEN_ERROR });
          }
        },
      }
    );
  });

  return (
    <div className="w-138">
      <div className="text-center text-xl font-medium tracking-normal">Lead delivery schedule</div>
      <div className="mt-2 text-center text-sm font-normal">
        Please provide the specific days and times your campaign will run. You may still receive some leads outside this
        schedule.
      </div>

      <div className="mt-8 flex justify-center">
        <div>
          <div className="text-xs text-slate-700">{timeZoneValue}</div>
          <div className="flex items-center justify-between">
            <FormProvider {...methods}>
              <div>
                {getKeys(defaultSchedule).map((day, index) => (
                  <SchedulingDayChangeable
                    index={index}
                    timeOptions={timeOptions}
                    key={day}
                    name={day}
                    period={defaultSchedule[day]}
                    schedule={defaultSchedule}
                    className="mt-2"
                  />
                ))}
              </div>
            </FormProvider>
          </div>
        </div>
      </div>

      <div className="mt-8 flex justify-between">
        <Button
          type="button"
          onClick={onBack}
          variant="outline-second"
          {...getDataAutoTestAttributes(['button-create-wizard-back'])}
        >
          Back
        </Button>
        <Button
          loading={isLoadingUpdate || isLoadingSubmit}
          disabled={!isAnyDaySelected}
          onClick={onSubmit}
          type="button"
          {...getDataAutoTestAttributes(['button-create-wizard-next'])}
        >
          {isSalesCampaignType ? 'Create' : 'Next'}
        </Button>
      </div>
    </div>
  );
}
